import styled from "styled-components";
import { ContainerComponent, wrap, Wrapper } from 'AppReact';
import Schema, { required } from 'AppReact/schema';
import { InputError, Input } from 'molecules';
import { SectionLabel, Seprator } from './../../../../common/atoms';
import { DeleteButton, AddSection, GoogleSearchInput } from './../../../../common/molecules';
import { FieldWrapper } from './../../../../common/wrappers';
import { employmentInformationSchema, defaultEmploymentInformation } from '../../../../types';
import Controller from './controller';
import { GenericWrapper } from 'wrappers';
import { isLastItem, showAddressFields } from "../../../../utils";
import { US_STATES } from "utils/statesCodeOptions";
import { Dropdown } from 'containers';

const WrappedInput = wrap(Input, FieldWrapper);
const WrappedInputError = wrap(InputError, FieldWrapper);
const WrappedGoogleSearchInput = wrap(GoogleSearchInput, FieldWrapper);
const WrappedDropdown = wrap(Dropdown, FieldWrapper);

const EmploymentInformation = {
    controller: Controller,
    name: "EmploymentInfo",
    initialProps: ({ language, isEditMode, onReview, employmentInformation, employmentInformations }) => ({ language, isEditMode, onReview, employmentInformation, employmentInformations }),
    props: {
        employmentInformation: Schema(employmentInformationSchema, 'object', { defaultValue: defaultEmploymentInformation }),
        employmentInformations: Schema(employmentInformationSchema, 'array'),
        onReview: Schema('boolean', 'primitive'),
        isEditMode: Schema('boolean', 'primitive'),
        language: Schema( {}, 'object'),
    },
    elements: [
        {
            wrapper: GenericWrapper,
            name: "Wrapper",
            elements: [
                {
                    component: WrappedInput,
                    name: 'EmployerName',
                    bindings: ({ language, employmentInformations, employmentInformation }) => ({ employmentInformations, employmentInformation, label: `${language?.employer || 'Employer Name'}:`, value: employmentInformation.employerName })
                },
                {
                    component: WrappedInputError,
                    name: 'NameError',
                    bindings: ({ employmentInformation }) => ({ error: employmentInformation.employerNameError }),
                    renderIf: ({ employmentInformation, onReview }) => !!employmentInformation.employerNameError && !onReview,
                },
                {
                    component: WrappedInput,
                    name: 'Address',
                    renderIf:({ isEditMode, employmentInformation }) =>  !employmentInformation?.address?.hasOwnProperty('address') && isEditMode,
                    bindings: ({ employmentInformations, employmentInformation }) => 
                        ({ employmentInformations, employmentInformation, label: 'Address:', value: employmentInformation?.address || "" })
                },
                {
                    component: WrappedInputError,
                    name: 'AddressError',
                    renderIf: ({ isEditMode, employmentInformation, onReview }) => isEditMode && !!employmentInformation?.addressError && !onReview,
                    bindings: ({ language, employmentInformation }) => ({ error: employmentInformation?.addressError || "" })
                },
                {
                    component: WrappedGoogleSearchInput,
                    name: 'Location',
                    renderIf: ({ isEditMode }) => !isEditMode,
                    bindings: ({ employmentInformations, employmentInformation }) => 
                        ({ employmentInformations, resourceObj:employmentInformation, label: '', value: employmentInformation.address || employmentInformation.address.address })
                },
                {
                    component: WrappedInputError,
                    name: 'LocationError',
                    bindings: ({ employmentInformation }) => ({error: employmentInformation?.address?.addressError}),
                    renderIf: ({ employmentInformation, onReview }) => !!employmentInformation?.address?.addressError && !onReview,
                },
                {
                    wrapper: Wrapper(styled.div`display: flex; flex-wrap: wrap`),
                    name: "AddressDetails",
                    renderIf: ({ isEditMode, employmentInformation }) => {
                        const shouldshow = showAddressFields(employmentInformation)
                        return shouldshow && !isEditMode
                    },
                    elements: [
                        {
                            component: WrappedInput,
                            name: 'City',
                            bindings: ({ employmentInformation, employmentInformations, }) => {
                                return ({ employmentInformation, employmentInformations, label: 'City:', value: employmentInformation.address.city || "" })
                            }
                        },
                        {
                            component: WrappedInputError,
                            name: 'CityError',
                            bindings: ({ employmentInformation }) => ({error: employmentInformation?.address?.cityError || ""}),
                            renderIf: ({ employmentInformation, selectedTabId }) => {
                                return !!employmentInformation?.address?.cityError && selectedTabId !== 'review'
                            },
                        },
                        {
                            component: WrappedDropdown,
                            name: 'State',
                            bindings: ({ employmentInformation, employmentInformations,  }) => {
                                return ({ search: true,employmentInformation, employmentInformations, label: 'State: ', selectedOptionId: employmentInformation?.address?.state, options: US_STATES })
                            }
                        },
                        {
                            component: WrappedInputError,
                            name: 'StateError',
                            bindings: ({ employmentInformation }) => ({error: employmentInformation?.address?.stateError || ""}),
                            renderIf: ({ employmentInformation, selectedTabId }) => !!employmentInformation?.address?.stateError && selectedTabId !== 'review',
                        },
                        {
                            component: WrappedInput,
                            name: 'Zip',
                            bindings: ({ employmentInformation, employmentInformations }) => ({ employmentInformations, employmentInformation, label: 'Zip:', value: employmentInformation.address.zip || "" })
                        },
                        {
                            component: WrappedInputError,
                            name: 'ZipError',
                            bindings: ({ employmentInformation }) => ({error: employmentInformation?.address?.zipError || ""}),
                            renderIf: ({ employmentInformation, selectedTabId }) => !!employmentInformation?.address?.zipError && selectedTabId !== 'review',
                        },
                    ]
                },
                {
                    component: WrappedInput,
                    name: 'PhoneNumber',
                    bindings: ({ employmentInformations, employmentInformation }) => ({ employmentInformations, employmentInformation, label: 'Phone Number:', value: employmentInformation.phoneNumber })
                },
                {
                    component: WrappedInputError,
                    name: 'PhoneNumberError',
                    bindings: ({ employmentInformation }) => ({ error: employmentInformation.phoneNumberError }),
                    renderIf: ({ employmentInformation, onReview }) => !!employmentInformation.phoneNumberError && !onReview,
                },
                {
                    component: WrappedInput,
                    name: 'Email',
                    bindings: ({ employmentInformations, employmentInformation }) => ({ employmentInformations, employmentInformation, label: 'Email:', value: employmentInformation.email })
                },
                {
                    component: WrappedInputError,
                    name: 'EmailError',
                    bindings: ({ employmentInformation }) => ({ error: employmentInformation.emailError }),
                    renderIf: ({ employmentInformation, onReview }) => !!employmentInformation.emailError && !onReview,
                },
                {
                    component: WrappedInput,
                    name: 'SupervisorName',
                    bindings: ({ language, employmentInformations, employmentInformation }) => ({ employmentInformations, employmentInformation, label:  `${language?.supervisor_name || 'Supervisor Name'}:`, value: employmentInformation.supervisorName })
                },
                {
                    component: WrappedInputError,
                    name: 'SupervisorNameError',
                    bindings: ({ employmentInformation }) => ({ error: employmentInformation.supervisorNameError }),
                    renderIf: ({ employmentInformation, onReview }) => !!employmentInformation.supervisorNameError && !onReview,
                },
                {
                    component: WrappedInput,
                    name: 'DurationOfEmployment',
                    bindings: ({ language, employmentInformations, employmentInformation }) => ({ employmentInformations, employmentInformation, label: `${language?.employment_duration || 'Duration of Employment'}:`, value: employmentInformation.durationOfEmployment })
                },
                {
                    component: WrappedInputError,
                    name: 'DurationOfEmploymentError',
                    bindings: ({ employmentInformation }) => ({ error: employmentInformation.durationOfEmploymentError }),
                    renderIf: ({ employmentInformation, onReview }) => !!employmentInformation.durationOfEmploymentError && !onReview,
                },
                {
                    component: WrappedInput,
                    name: 'MonthlyIncome',
                    bindings: ({ language, employmentInformations, employmentInformation }) => ({ employmentInformations, employmentInformation, label: `${language?.monthly_income || 'Monthly Income'}:` , value: String(employmentInformation.monthlyIncome) })
                },
                {
                    component: WrappedInputError,
                    name: 'MonthlyIncomeError',
                    bindings: ({ employmentInformation }) => ({ error: employmentInformation.monthlyIncomeError }),
                    renderIf: ({ employmentInformation, onReview }) => !!employmentInformation.monthlyIncomeError && !onReview,
                },
                {
                    component: AddSection,
                    name: 'AddIncome',
                    isButton: true,
                    renderIf: ({ onReview, employmentInformation }) => !employmentInformation.hasExtraIncome && !onReview,
                    bindings: ({ language, employmentInformations, employmentInformation }) => ({ employmentInformations, employmentInformation, label: `${language?.other_income || 'Other Income'}`}),
                },
                {
                    wrapper: GenericWrapper,
                    name: "OtherIncome",
                    renderIf: ({ employmentInformation }) => employmentInformation.hasExtraIncome,
                    elements: [
                        {
                            wrapper: Wrapper(styled.div`display: flex; justify-content: space-between;`),
                            name: 'Header',
                            elements: [
                                {
                                    component: SectionLabel,
                                    name: 'SectionTitle',
                                    displayValue: () => "Other Income",
                                    bindings: ({ }) => ({})
                                },
                                {
                                    component: DeleteButton,
                                    name: 'Delete',
                                    renderIf: ({ onReview }) => !onReview,
                                    bindings: ({ employmentInformations, employmentInformation }) => ({ employmentInformations, employmentInformation })
                                },
                            ]
                        },
                        {
                            component: WrappedInput,
                            name: 'Description',
                            bindings: ({ language, employmentInformations, employmentInformation }) => 
                                ({ employmentInformations, employmentInformation, label: `${language?.description || 'Description'}:`, value: employmentInformation?.extra_income_description || '' })

                        },
                        {
                            component: WrappedInputError,
                            name: 'DescriptionError',
                            bindings: ({ employmentInformation }) => ({ error: employmentInformation.extra_income_descriptionError }),
                            renderIf: ({ employmentInformation, onReview }) => !!employmentInformation.extra_income_descriptionError && !onReview,
                        },
                        {
                            component: WrappedInput,
                            name: 'ExtraMonthlyIncome',
                            bindings: ({ language, employmentInformations, employmentInformation }) => ({ employmentInformations, employmentInformation, label: `${language?.monthly_income || 'Monthly Income'}:`, value: String(employmentInformation?.extra_income || '') })
                        },
                        {
                            component: WrappedInputError,
                            name: 'ExtraMonthlyIncomeError',
                            bindings: ({ employmentInformation }) => ({ error: employmentInformation.extra_incomeError }),
                            renderIf: ({ employmentInformation, onReview }) => !!employmentInformation.extra_incomeError && !onReview,
                        },
                    ]
                }
            ]
        },
        {
            component: Seprator,
            name: 'Seprator',
            displayValue: () => '',
            renderIf: ({ employmentInformation, employmentInformations }) => isLastItem({ resource:employmentInformation, resourceList:employmentInformations })
        },

    ],
}

export default ContainerComponent(EmploymentInformation);
